import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, cn } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { HttpClient } from "@dl/web-core-js/api";
import { PipelineLotClient, WEEKS_TO_OOS_UNBOUNDED, type PipelineLotDTO } from "../api/pipeline";
import { CoverBar } from "../components/CoverBar";
import {
  imageUrl,
  LazyProductClient,
  netQuantity,
  velocityOf,
  type LazyProductDTO,
} from "../api/product";
import { SETTINGS_PATHS, bandFor, bandLabel, settingsClient, type RankBandDTO } from "../api/settings";
import { NexpimView } from "../views";
import { ProductLink } from "../components/ProductLink";

/**
 * An open production project, straight from the SIGM project table — the
 * `prj_usr_*` columns carry the shop-floor meanings the Java DTO names:
 * date1/2 = particle/MDF cut planner, date5 = arrived at Laval,
 * flag1 = produced at Terrebonne, string1/2 = notes/comments,
 * float2 = the nexpim pipeline lot id.
 */
interface ProductionProjectDTO {
  id: number;
  prj_no?: number | null;
  prj_name?: string | null;
  prj_req_qty?: number | null;
  prj_start_dt?: string | null;
  prj_del_dt?: string | null;
  prj_usr_date1?: string | null;
  prj_usr_date2?: string | null;
  prj_usr_date5?: string | null;
  prj_usr_flag1?: boolean | null;
  prj_usr_string1?: string | null;
  prj_usr_string2?: string | null;
  prj_usr_float2?: number | null;
  part?: { no?: string | null; description1?: string | null } | null;
}

class ProjectClient {
  constructor(private readonly http: HttpClient) {}
  getOpen(): Promise<ProductionProjectDTO[]> {
    return this.http.get<ProductionProjectDTO[]>("/production/project/open");
  }
}

type ProjectStatus = "PENDING" | "IN PRODUCTION" | "READY TO SHIP" | "PACKAGING";

/**
 * Declared, so the filter offers every status whether or not one is on the
 * board today. Left to the data, a day with no project in PENDING would drop
 * PENDING from the list, and it would look removed rather than empty.
 */
const PROJECT_STATUSES: ProjectStatus[] = ["PENDING", "IN PRODUCTION", "READY TO SHIP", "PACKAGING"];

/** The Vaadin status ladder, derived from the shop-floor dates and flags. */
function projectStatus(project: ProductionProjectDTO): ProjectStatus {
  if (project.prj_usr_flag1) return "PACKAGING";
  if (project.prj_usr_date5?.trim()) return "READY TO SHIP";
  if (project.prj_usr_date1?.trim() || project.prj_usr_date2?.trim()) return "IN PRODUCTION";
  return "PENDING";
}

/** Project part numbers carry a `90-` production prefix the catalogue lacks. */
function productSku(project: ProductionProjectDTO): string {
  return (project.part?.no ?? "").replace("90-", "");
}

const truncate1 = (value: number) => Math.trunc(value * 10) / 10;

interface Row extends ProductionProjectDTO {
  product: LazyProductDTO | null;
  lot: PipelineLotDTO | null;
  /** Weeks of cover the pipeline plans this product against. */
  target_weeks: number;
  /** The product's rank band, when it is a bounded one ("Top 25"); the open band gets no badge. */
  band_label: string | null;
}

/**
 * Open production projects, joined to the catalogue (thumbnail, stock,
 * velocity) and to their pipeline lot (NEW/JUST LAUNCHED, paint). The
 * Laval/Terrebonne split matters on the floor — the two facilities plan
 * separately — so both counts sit in the header, as they did in Vaadin.
 */
export function ProjectsPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const allowed = hasView(NexpimView.PRODUCTION_PROJECT);

  const projects = useMemo(() => new ProjectClient(http), [http]);
  const lazy = useMemo(() => new LazyProductClient(http), [http]);
  const lots = useMemo(() => new PipelineLotClient(http), [http]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["production", "projects-open"],
    queryFn: async () => {
      const open = await projects.getOpen();

      const skus = [...new Set(open.map(productSku).filter(Boolean))];
      const lotIds = [
        ...new Set(
          open
            .map((row) => Math.trunc(row.prj_usr_float2 ?? 0))
            .filter((lotId) => lotId !== 0),
        ),
      ];
      const [products, lotRows, bands] = await Promise.all([
        skus.length ? lazy.bySkus(skus) : Promise.resolve([]),
        lotIds.length ? lots.findByIds(lotIds) : Promise.resolve([]),
        settingsClient<RankBandDTO>(http as HttpClient, SETTINGS_PATHS.rankBands).findAll(),
      ]);
      const bySku = new Map(products.map((product) => [product.metadata?.sku ?? "", product]));
      const byLotId = new Map(lotRows.map((lot) => [lot.id, lot]));

      return open.map((row): Row => {
        const product = bySku.get(productSku(row)) ?? null;
        const lot = byLotId.get(Math.trunc(row.prj_usr_float2 ?? 0)) ?? null;
        // The lot's own target when it has one — that is what the pipeline
        // actually planned against — else the target its rank band gives.
        const band = bandFor(bands, product?.kpi_sales?.sales_rank);
        const target_weeks = lot?.planning_data?.target_weeks || (band?.target_weeks ?? 0);
        const band_label = product?.kpi_sales?.sales_rank && band && band.rank_to !== null ? bandLabel(band, t) : null;
        return { ...row, product, lot, target_weeks, band_label };
      });
    },
    enabled: allowed,
    staleTime: 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<Row>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => {
          const url = imageUrl(row.product?.image);
          return url ? (
            <img src={url} alt="" loading="lazy" className="size-12 rounded object-cover" />
          ) : (
            <span className="block size-12 rounded bg-surface-sunk" />
          );
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "part",
        (row) => `${row.part?.no ?? ""} ${row.part?.description1 ?? ""}`,
        (row) => (
          <span className="flex flex-col py-0.5">
            <span className="flex items-center gap-1.5">
              <ProductLink id={row.product?.id} className="font-medium">
                {row.part?.no}
              </ProductLink>
              {row.lot?.metadata?.first_batch ? (
                <Badge variant="success" size="sm">
                  {t("pipeline.just-launched", "JUST LAUNCHED")}
                </Badge>
              ) : row.lot?.metadata?.new_product ? (
                <Badge variant="success" size="sm">
                  {t("pipeline.new", "NEW")}
                </Badge>
              ) : null}
              {row.band_label && (
                <Badge variant="accent" size="sm">
                  {row.band_label.toUpperCase()}
                </Badge>
              )}
            </span>
            <span className="text-[11px] text-ink-faint">{row.part?.description1}</span>
          </span>
        ),
        { headerKey: "column.part", priority: 1 },
      ),
      c.custom(
        "project",
        (row) => String(row.prj_no ?? ""),
        (row) => (
          <span className="flex flex-col py-0.5 tabular">
            {row.prj_no}
            <span className="text-[11px] text-ink-faint">
              {row.lot
                ? `${t("project.pipeline-lot", "Pipeline lot")}: ${row.lot.id}`
                : t("project.not-planned", "Not planned")}
            </span>
          </span>
        ),
        { headerKey: "column.project", priority: 2 },
      ),
      c.custom(
        "status",
        (row) => projectStatus(row),
        (row) => {
          const status = projectStatus(row);
          const variant =
            status === "PACKAGING"
              ? "success"
              : status === "READY TO SHIP"
                ? "accent"
                : status === "IN PRODUCTION"
                  ? "neutral"
                  : "warning";
          return (
            <Badge variant={variant} size="sm">
              {status}
            </Badge>
          );
        },
        {
          headerKey: "column.status",
          filter: "multiselect",
          filterOptions: PROJECT_STATUSES.map((status) => ({ value: status, label: status })),
          priority: 2,
        },
      ),
      c.custom(
        "location",
        (row) => (row.prj_usr_flag1 ? "TERREBONNE" : "LAVAL"),
        (row) => (
          <Badge variant={row.prj_usr_flag1 ? "success" : "neutral"} size="sm">
            {row.prj_usr_flag1 ? "TERREBONNE" : "LAVAL"}
          </Badge>
        ),
        { headerKey: "column.location", filter: "select", priority: 3 },
      ),
      c.custom(
        "prod_qty",
        (row) => Math.trunc(row.prj_req_qty ?? 0),
        (row) => (
          <Badge variant="neutral" size="sm">
            {Math.trunc(row.prj_req_qty ?? 0)}
          </Badge>
        ),
        { headerKey: "column.production.qty", filter: "none", priority: 3 },
      ),
      // Weeks to OOS: the pipeline's bar and the weeks on the first line, the
      // stock it rests on underneath. The weekly sales sit in their own column
      // beside it, large, so the board sorts into best sellers in one click.
      // Same bar and same target as the pipeline page.
      c.custom(
        "weeks_to_oos",
        (row) => weeksToOos(row.product),
        (row) => {
          if (!row.product) return <span />;
          const net = netQuantity(row.product);
          const weeks = weeksToOos(row.product);
          const noDemand = velocityOf(row.product) <= 0;
          const out = net <= 0;
          const lowStock = !out && !noDemand && weeks < row.target_weeks;
          return (
            <span className="flex flex-col gap-0.5 leading-tight">
              <span className="flex items-center gap-2">
                <CoverBar weeks={noDemand ? WEEKS_TO_OOS_UNBOUNDED : weeks} target={row.target_weeks} />
                <span
                  className={
                    out
                      ? "tabular text-[13px] font-medium text-risk"
                      : noDemand
                        ? "tabular text-[13px] text-ink-faint"
                        : lowStock
                          ? "tabular text-[13px] font-medium text-warn"
                          : "tabular text-[13px] text-ink"
                  }
                  title={
                    noDemand
                      ? t("pipeline.no-demand", "No recent demand, nothing to run out of.")
                      : t("pipeline.runway.hint", "Cover against a {{window}} week planning window.", {
                          window: row.target_weeks,
                        })
                  }
                >
                  {out
                    ? t("transfer.out", "OUT")
                    : noDemand
                      ? ""
                      : t("pipeline.weeks-short", "{{count}} wks", { count: weeks })}
                </span>
              </span>
              <span className={cn("text-[11px] tabular", out ? "text-risk" : "text-ink-muted")}>
                {t("project.cover.available", "{{count}} available", { count: net })}
              </span>
            </span>
          );
        },
        { headerKey: "column.weeks-to-oos", filter: "none", align: "left", priority: 2 },
      ),
      c.custom(
        "weekly_sales",
        (row) => (row.product ? truncate1(velocityOf(row.product)) : null),
        (row) => {
          if (!row.product) return <span />;
          const value = truncate1(velocityOf(row.product));
          return <span className="tabular text-[15px] font-semibold text-ink">{value}</span>;
        },
        { headerKey: "column.weekly-sales", filter: "range", align: "right", priority: 2 },
      ),
      // Vaadin's "Start date" column reads the SIGM delivery date.
      c.text("start_date", (row) => row.prj_del_dt ?? "", {
        headerKey: "column.date.start",
        filter: "daterange",
        priority: 3,
      }),
      c.text("cut_particle", (row) => row.prj_usr_date1 ?? "", {
        headerKey: "column.date.cut.particle",
        filter: "daterange",
      }),
      c.text("cut_mdf", (row) => row.prj_usr_date2 ?? "", {
        headerKey: "column.date.cut.mdf",
        filter: "daterange",
      }),
      c.custom(
        "paint",
        (row) => (row.lot?.metadata?.has_paint ? "YES" : "NO"),
        (row) =>
          row.lot?.metadata?.has_paint ? (
            <Badge variant="success" size="sm">
              PAINT
            </Badge>
          ) : (
            <span />
          ),
        { headerKey: "column.paint", filter: "select" },
      ),
      c.custom(
        "notes",
        (row) => `${row.prj_usr_string1 ?? ""} ${row.prj_usr_string2 ?? ""}`,
        (row) => (
          <span className="flex flex-col py-0.5">
            {row.prj_usr_string1}
            <span className="text-[11px] text-ink-faint">{row.prj_usr_string2}</span>
          </span>
        ),
        { headerKey: "column.notes" },
      ),
    ];
  }, [t]);

  // Vaadin's default sort: start (delivery) date, then project number.
  const rows = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) =>
          (a.prj_del_dt ?? "").localeCompare(b.prj_del_dt ?? "") ||
          (a.prj_no ?? 0) - (b.prj_no ?? 0),
      ),
    [data],
  );

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.production-projects", "Projects")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const laval = (data ?? []).filter((row) => !row.prj_usr_flag1).length;
  const terrebonne = (data ?? []).length - laval;

  return (
    <>
      <PageHeader
        title={t("navigation.item.production-projects", "Projects")}
        subtitle={t("project.subtitle", "Open production projects.")}
        badges={
          <>
            <Badge variant="neutral" size="sm">
              {t("project.laval", "Laval: {{count}}", { count: laval })}
            </Badge>
            <Badge variant="success" size="sm">
              {t("project.terrebonne", "Terrebonne: {{count}}", { count: terrebonne })}
            </Badge>
          </>
        }
      />

      {error ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={cols}
          getRowId={(row) => String(row.id)}
          loading={isLoading}
          emptyMessage={t("project.empty", "No open projects.")}
        />
      )}
    </>
  );
}

/**
 * Weeks of cover, as the column sorts on it: out of stock first, then by
 * cover, no-demand rows last — the pipeline's sentinel keeps them at the
 * bottom rather than tangled with the urgent ones at zero.
 */
function weeksToOos(product: LazyProductDTO | null): number {
  if (!product) return WEEKS_TO_OOS_UNBOUNDED;
  const velocity = velocityOf(product);
  if (velocity <= 0) return WEEKS_TO_OOS_UNBOUNDED;
  const net = netQuantity(product);
  return net <= 0 ? 0 : Math.trunc(net / velocity);
}
